import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Select,
  TextField,
  Typography,
} from "@suraasa/placebo-ui"
import api from "api"
import {
  LeadQualification,
  qualificationFormatChoices,
  qualificationLevelChoices,
  QualificationType,
  qualificationTypeChoices,
} from "api/resources/lead/types"
import { APIError } from "api/utils"
import { format } from "date-fns"
import { Controller, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { mapErrors } from "utils/helpers"
import { useResource } from "utils/hooks/useResource"

type FormData = {
  name: string | null
  level: string | null
  institutionName: string | null
  startDate: string | null
  endDate: string | null
  subjectId: string | null
  qualificationFormat: string | null
  qualificationType: string | null
}

type Props = {
  open: boolean
  handleClose: () => void
  suraasaLeadId?: string | number
  onUpdate?: () => void
  data?: LeadQualification | null
  defaultQualificationType?: QualificationType | null
}

const QualificationFormDialog = ({
  open,
  handleClose,
  onUpdate,
  data,
  suraasaLeadId,
  defaultQualificationType,
}: Props) => {
  const { subjects } = useResource(["subjects"])

  const {
    endDate,
    institutionName,
    startDate,
    subjectId,
    id,
    level,
    name,
    qualificationType,
    qualificationFormat,
  } = data || {}

  const {
    register,
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm<FormData>({
    defaultValues: {
      endDate: endDate ? format(new Date(endDate), "yyyy-MM-dd") : "",
      startDate: startDate ? format(new Date(startDate), "yyyy-MM-dd") : "",
      institutionName,
      subjectId: subjectId,
      name: name,
      level,
      qualificationFormat,
      qualificationType: qualificationType || defaultQualificationType,
    },
  })

  const onSubmit = handleSubmit(async values => {
    const formData = {
      name: values.name,
      level: values.level,
      institutionName: values.institutionName,
      startDate: values.startDate
        ? format(new Date(values.startDate), "yyyy-MM-dd")
        : null,
      endDate: values.endDate
        ? format(new Date(values.endDate), "yyyy-MM-dd")
        : null,
      subjectId: values.subjectId,
      qualificationFormat: values.qualificationFormat,
      qualificationType: values.qualificationType,
    }

    // Filtering out blank values because we don't want to send them to backend.
    Object.entries(formData).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) {
        delete formData[k as keyof typeof formData]
      }
    })

    try {
      if (id) {
        await api.lead.updateQualification({
          data: formData,
          params: {
            qualificationId: id,
          },
        })
        toast.info("Qualification updated.")
      } else {
        if (!suraasaLeadId) {
          throw new Error("suraasaLeadId is required for adding qualifications")
        }
        await api.lead.addQualification({
          data: formData,
          params: {
            suraasaLeadId: suraasaLeadId,
          },
        })
        toast.info("Qualification added.")
      }

      handleClose()

      await onUpdate?.()
    } catch (e) {
      if (e instanceof APIError) {
        const { errors } = e
        if (errors.message) {
          toast.error(errors.message)
        }
        if (errors.fieldErrors) {
          mapErrors(setError, errors, [
            ["name", "name"],
            ["endDate"],
            ["startDate"],
            ["institutionName"],
            ["subjectId"],
            ["level"],
            ["qualificationFormat"],
            ["qualificationType"],
          ])
        }
      }
    }
  })

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <Typography variant="strong">
            {data ? "Update Qualification" : "Add Qualification"}
          </Typography>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-2">
            <TextField
              autoFocus
              errors={errors.name?.message}
              label="Qualification Title"
              placeholder="Ex: B. Ed."
              {...register("name", { required: "Required" })}
            />
            <TextField
              errors={errors.institutionName?.message}
              label="Institution Name"
              placeholder="Ex: Delhi University"
              {...register("institutionName")}
            />
            <Controller
              control={control}
              name="qualificationType"
              render={({ field: { onChange, value } }) => (
                <Select
                  // @ts-expect-error this is fine
                  errors={errors.qualificationType?.message}
                  label="Qualification Type"
                  placeholder="Ex: Subject Qualification"
                  {...register("qualificationType")}
                  getOptionLabel={item => item.label}
                  getOptionValue={item => item.value.toString()}
                  options={qualificationTypeChoices}
                  value={
                    value
                      ? qualificationTypeChoices.find(
                          item => item.value == value
                        )
                      : null
                  }
                  onChange={val => {
                    onChange(val?.value)
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="level"
              render={({ field: { onChange, value } }) => (
                <Select
                  // @ts-expect-error this is fine
                  errors={errors.level?.message}
                  label="Qualification Level"
                  placeholder="Ex: Under Graduate"
                  {...register("level")}
                  getOptionLabel={item => item.label}
                  getOptionValue={item => item.value.toString()}
                  options={qualificationLevelChoices}
                  value={
                    value
                      ? qualificationLevelChoices.find(
                          item => item.value == value
                        )
                      : null
                  }
                  onChange={val => {
                    onChange(val?.value)
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="qualificationFormat"
              render={({ field: { onChange, value } }) => (
                <Select
                  placeholder="Ex: Regular"
                  // @ts-expect-error this is fine
                  errors={errors.qualificationFormat?.message}
                  label="Qualification Format"
                  {...register("qualificationFormat")}
                  getOptionLabel={item => item.label}
                  getOptionValue={item => item.value.toString()}
                  options={qualificationFormatChoices}
                  value={
                    value
                      ? qualificationFormatChoices.find(
                          item => item.value == value
                        )
                      : null
                  }
                  onChange={val => {
                    onChange(val?.value)
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="subjectId"
              render={({ field: { onChange, value } }) => (
                <Select
                  // @ts-expect-error this is fine
                  errors={errors.subjectId?.message}
                  label="Subject"
                  placeholder="Ex: English"
                  {...register("subjectId")}
                  getOptionLabel={item => item.name}
                  getOptionValue={item => item.uuid.toString()}
                  options={subjects}
                  value={
                    value ? subjects.find(item => item.uuid == value) : null
                  }
                  onChange={val => {
                    onChange(val?.uuid)
                  }}
                />
              )}
            />

            <div className="flex gap-2">
              <TextField
                {...register("startDate")}
                label="Start Date"
                errors={errors.startDate?.message}
                type="date"
              />
              <TextField
                label="End Date"
                {...register("endDate")}
                errors={errors.endDate?.message}
                type="date"
              />
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <div>
            <Button
              variant="outlined"
              onClick={() => {
                handleClose()
              }}
              className="mr-1.25 text-black"
              size="sm"
            >
              Cancel
            </Button>
            <Button onClick={onSubmit} size="sm" loading={isSubmitting}>
              {id ? "Update" : "Add"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default QualificationFormDialog
